import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaEnvelope, FaEnvelopeOpenText } from 'react-icons/fa';
import { getExperienceLine, getModuleTitle, nudge, playSoftTone } from './openingUtils';

const EnvelopeExperience = ({ modules, renderModules, openingMode, giftIntro = {} }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [opened, setOpened] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [flightDirection, setFlightDirection] = useState(1);
  const moduleStageRef = useRef(null);

  useEffect(() => {
    setActiveIndex(0);
    setOpened(false);
    setIsFlying(false);
  }, [openingMode, modules?.length]);

  const activeModule = modules[activeIndex] || modules[0];
  const introTitle = giftIntro.title || 'Pull out one private note at a time';
  const introCopy = giftIntro.subtitle ||
    'Each letter flies in like a fresh envelope, then opens the memory attached to it.';

  const openEnvelope = () => {
    nudge(18);
    playSoftTone(587, 0.12);
    setOpened(true);
  };

  const goToLetter = (targetIndex) => {
    if (targetIndex < 0 || targetIndex >= modules.length || targetIndex === activeIndex || isFlying) return;
    nudge(12);
    playSoftTone(698, 0.1);
    setFlightDirection(targetIndex > activeIndex ? 1 : -1);
    setIsFlying(true);
    setTimeout(() => {
      setActiveIndex(targetIndex);
      setIsFlying(false);
      window.requestAnimationFrame(() => {
        moduleStageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }, 360);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 space-y-6">
      <div className="lumirae-opening-card text-center [perspective:1000px]">
        <div className="relative overflow-hidden px-6 pt-7">
          <div className="absolute -left-12 top-10 h-28 w-28 rounded-full blur-2xl" style={{ backgroundColor: 'var(--lumirae-accent-soft)' }} />
          <div className="absolute -right-10 bottom-8 h-28 w-28 rounded-full bg-purple-100/50 blur-2xl" />
          <div className="lumirae-opening-label relative mb-4 flex items-center justify-center gap-2">
            <FaEnvelopeOpenText /> Open When
          </div>
          <h2 className="lumirae-opening-title relative mx-auto max-w-xs text-3xl leading-tight">
            {introTitle}
          </h2>
          <p className="lumirae-opening-copy relative mx-auto mt-3 max-w-xs">
            {introCopy}
          </p>
        </div>
        <button
          onClick={openEnvelope}
          className="mx-auto block w-full max-w-sm"
          aria-label={opened ? 'Envelope opened' : 'Open the envelope'}
        >
          <div className="relative mx-auto h-72 w-full max-w-sm">
            <motion.div
              className="absolute left-7 right-7 top-8 h-44 rounded-[2rem] border-4 border-white bg-white/45 shadow-inner"
              animate={{ opacity: [0.45, 0.8, 0.45] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-40 rounded-b-[2rem] border-4 border-white shadow-2xl"
              style={{ backgroundColor: 'color-mix(in srgb, var(--lumirae-accent-soft) 72%, white)' }}
              animate={{ y: opened ? 10 : 0 }}
            />
            <motion.div
              className="absolute bottom-24 left-0 right-0 h-32 origin-bottom rounded-t-[2rem] border-4 border-white"
              style={{ backgroundColor: 'color-mix(in srgb, var(--lumirae-accent-primary) 44%, white)' }}
              animate={opened ? { rotateX: -145, y: -8 } : { rotateX: [0, 10, 0] }}
              transition={{ duration: opened ? 0.8 : 3, repeat: opened ? 0 : Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              key={`paper-${activeIndex}`}
              className="absolute bottom-10 left-8 right-8 rounded-[1.5rem] border p-6 shadow-xl"
              style={{ backgroundColor: 'var(--lumirae-surface-raised)', borderColor: 'var(--lumirae-border-default)' }}
              initial={{ x: opened ? -80 * flightDirection : 0, opacity: opened ? 0 : 1, rotate: opened ? -4 * flightDirection : 0 }}
              animate={
                isFlying ?
                  { x: 360 * flightDirection, y: -120, opacity: 0, rotate: 18 * flightDirection, scale: 0.92 } :
                  opened ? { x: 0, y: -92, scale: 1.02, opacity: 1, rotate: 0 } : { x: 0, y: 0, scale: 0.92, opacity: 1 }
              }
              transition={{ duration: isFlying ? 0.35 : 0.85, ease: 'easeOut' }}
            >
              <FaEnvelopeOpenText className="mx-auto mb-3 text-3xl" style={{ color: 'var(--lumirae-accent-primary)' }} />
              <div className="lumirae-opening-label">
                {opened ? `Letter ${activeIndex + 1}` : 'Tap to open'}
              </div>
              <div className="mt-2 truncate text-sm font-black uppercase" style={{ color: 'var(--lumirae-text-primary)' }}>
                {activeModule ? getModuleTitle(activeModule, activeIndex) : 'Sealed letter'}
              </div>
            </motion.div>
            <FaEnvelope className="absolute bottom-14 left-1/2 -translate-x-1/2 text-5xl opacity-40" style={{ color: 'var(--lumirae-accent-primary)' }} />
          </div>
        </button>
        {!opened && (
          <div className="lumirae-opening-pill mx-auto mt-2 inline-flex px-4 py-2 text-[8px] font-black uppercase tracking-[0.24em]">
            Sealed letter experience
          </div>
        )}
        <p className="lumirae-opening-copy mx-auto mt-3 max-w-xs">
          {opened ? getExperienceLine(activeIndex) : 'The note slides out before the memory appears.'}
        </p>
      </div>

      {opened && activeModule && (
        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id || activeModule._localId || activeIndex}
            initial={{ opacity: 0, y: 28, rotateX: -6 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="w-full overflow-visible"
            ref={moduleStageRef}
          >
            {renderModules([activeModule], 'full_page')}
          </motion.div>
        </AnimatePresence>
      )}

      {opened && (
        <div className="lumirae-opening-card mx-4 p-4">
          <div className="mb-3 flex justify-center gap-2">
            {modules.map((module, index) => (
              <button
                key={module.id || module._localId || index}
                type="button"
                onClick={() => goToLetter(index)}
                className={`h-9 min-w-10 rounded-[0.9rem] border-2 px-3 text-[8px] font-black uppercase tracking-widest transition-all ${
                  activeIndex === index ? 'border-white text-white shadow-md' : 'border-white bg-white/80'
                }`}
                style={activeIndex === index ? { backgroundColor: 'var(--lumirae-accent-primary)' } : { color: 'var(--lumirae-text-secondary)' }}
                aria-label={`Open letter ${index + 1}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        <div className="flex items-center justify-between gap-3 px-1">
          <button
            onClick={() => goToLetter(activeIndex - 1)}
            disabled={activeIndex === 0}
            className="lumirae-opening-button-secondary px-5 py-3 text-[9px] font-black uppercase tracking-widest disabled:opacity-30"
          >
            <FaChevronLeft />
          </button>
          <div className="lumirae-ds-meta">
            Letter {activeIndex + 1} / {modules.length}
          </div>
          <button
            onClick={() => goToLetter(activeIndex + 1)}
            disabled={activeIndex >= modules.length - 1}
            className="lumirae-opening-button px-5 py-3 text-[9px] font-black uppercase tracking-widest disabled:opacity-30"
          >
            Next Letter
          </button>
        </div>
        </div>
      )}
      {opened && modules.length > 1 && (
        <div className="lumirae-reader-step-dock" aria-label="Open When letter controls">
          <button
            type="button"
            onClick={() => goToLetter(activeIndex - 1)}
            disabled={activeIndex === 0}
            aria-label="Previous letter"
          >
            <FaChevronLeft />
          </button>
          <div>
            <span>Letter {activeIndex + 1} / {modules.length}</span>
            <strong>{activeModule ? getModuleTitle(activeModule, activeIndex) : 'Open When'}</strong>
          </div>
          <button
            type="button"
            onClick={() => goToLetter(activeIndex + 1)}
            disabled={activeIndex >= modules.length - 1}
            aria-label="Next letter"
          >
            <FaChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default EnvelopeExperience;
